/*
 * a2v-layout.js — WHERE things sit on an /audio-to-video frame.
 *
 * One pure module for the geometry of the text block (title + subtitle) and of
 * the visualiser, used by the preview, by EVERY export lane and by the drag
 * hit-test on the preview canvas. It draws nothing and knows no DOM: the caller
 * passes a `measure(text, px, weight)` function (canvas measureText in the
 * page, a fake in tests).
 *
 * WHY a separate module. Until 15.09.2026 text could only be top/centre/bottom,
 * always centred left-to-right and never wrapped; a power user making TikTok and
 * YouTube clips asked for exactly that (#1656: «Текст нужно сдвигать и влево и в
 * право… Мелкий шрифт… особенно для ютуба формата»), and every competitor sweep
 * (EchoWave, Kapwing, VEED, Canva) offers free X/Y placement by dragging. Free
 * placement multiplies the ways a preview can disagree with the file, and this
 * tool already carries scars from that class (#654, the lavfi drawtext lane that
 * rendered with its own fonts). So the geometry lives ONCE, here.
 *
 * THE COORDINATE: a fraction of the FREE SPACE, not of the frame.
 *   left = margin + x · (frameW − 2·margin − boxW)
 * x = 0 hugs the left margin, 1 the right, 0.5 is exactly centred — whatever the
 * box size. Consequences, each deliberate:
 *   • a block can never leave the frame, at any size, text length or aspect;
 *   • the old presets are plain values (top/centre/bottom = y 0 / 0.5 / 1; the
 *     visualiser's 3×3 grid = x,y ∈ {0, 0.5, 1}), so saved looks and templates
 *     need no migration and look the same;
 *   • switching 16:9 ↔ 9:16 keeps «bottom-left stays bottom-left».
 * When the box is wider than the free space there is nothing to choose: centred.
 */
(function (root) {
    'use strict';

    var TEXT_MARGIN_X = 0.05;
    /* Two vertical margins, on purpose. The presets (top / centre / bottom) keep
       the 8 % the renderer has always used, so every saved look and template
       lands on the same pixels as before. A block the user DRAGS may come to
       4 % of the edge — the visualiser's margin after #704, where 10 % made
       «bottom» read as half-way down. */
    var TEXT_PRESET_MARGIN_Y = 0.08;
    var TEXT_MARGIN_Y = 0.04;
    var TEXT_MAX_W = 0.90;         // lines wrap inside 90 % of the frame width
    var VIZ_MARGIN_X = 0.05;       // = the visualiser grid's historical 5 % side gap
    var VIZ_MARGIN_Y = 0.04;       // = its 4 % edge gap (#704)
    var VIZ_H = 0.30;              // visualiser box height, fraction of frame height

    var PRESET_Y = { top: 0, center: 0.5, bottom: 1 };

    function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
    function num(v) { return typeof v === 'number' && isFinite(v); }

    /* Place a box of `size` px along an axis of `total` px. */
    function place(frac, total, marginFrac, size) {
        var m = total * marginFrac;
        var free = total - 2 * m - size;
        if (free <= 0) return (total - size) / 2;
        return m + clamp01(frac) * free;
    }
    /* The inverse — for dragging: where a box that starts at `start` px sits,
       as a fraction of the free space. */
    function unplace(start, total, marginFrac, size) {
        var m = total * marginFrac;
        var free = total - 2 * m - size;
        if (free <= 0) return 0.5;
        return clamp01((start - m) / free);
    }

    /* Greedy word wrap. A single word wider than the line stays on its own line
       (it will be clipped by the frame, as any editor does, rather than broken
       mid-word into something unreadable). Existing line breaks are honoured. */
    function wrap(text, maxW, measureLine) {
        var out = [];
        String(text || '').split(/\r?\n/).forEach(function (para) {
            var words = para.split(/\s+/).filter(function (w) { return w.length; });
            if (!words.length) { if (para.length === 0 && out.length) out.push(''); return; }
            var line = words[0];
            for (var i = 1; i < words.length; i++) {
                var cand = line + ' ' + words[i];
                if (measureLine(cand) <= maxW) line = cand;
                else { out.push(line); line = words[i]; }
            }
            out.push(line);
        });
        return out;
    }

    /*
     * textLayout(o) → { box:{x,y,w,h}, lines:[{kind,text,x,baseline,px,width}], align, s }
     *   o.w, o.h                 frame size in the units being drawn
     *   o.titleText, o.titleSize px at the 1080-line reference (the tool's unit)
     *   o.subText,   o.subSize
     *   o.titleX, o.titleY       free-space fractions, or null → preset
     *   o.titlePos               'top' | 'center' | 'bottom' (preset when Y is null)
     *   o.titleAlign             'left' | 'center' | 'right'
     *   o.measure(text, px, weight) → width in the same units
     */
    function textLayout(o) {
        var w = o.w, h = o.h;
        var s = h / 1080;
        var measure = o.measure;
        var maxW = w * TEXT_MAX_W;
        var align = (o.titleAlign === 'left' || o.titleAlign === 'right') ? o.titleAlign : 'center';
        var rows = [];

        /* Wrap, and if a single WORD is still wider than the line (a big title in
           a 9:16 frame: «Капитан» at 200 px is wider than 720 px), shrink that
           text just enough for its widest line to fit. A clipped title in the
           exported file is a defect; a slightly smaller one is the size the
           frame can actually hold. */
        function fit(text, px, weight) {
            if (!text) return { px: 0, lines: [] };
            for (var k = 0; k < 4; k++) {
                var lines = wrap(text, maxW, function (t) { return measure(t, px, weight); });
                var widest = 0;
                lines.forEach(function (ln) { var lw = measure(ln, px, weight); if (lw > widest) widest = lw; });
                if (widest <= maxW + 0.5) return { px: px, lines: lines };
                px = px * (maxW / widest);
            }
            return { px: px, lines: wrap(text, maxW, function (t) { return measure(t, px, weight); }) };
        }
        var tFit = fit(o.titleText, o.titleText ? o.titleSize * s : 0, 700);
        var sFit = fit(o.subText, o.subText ? o.subSize * s : 0, 400);
        var tPx = tFit.px, sPx = sFit.px;
        var titleLines = tFit.lines, subLines = sFit.lines;

        // Vertical metrics. A single line keeps the historical block exactly
        // (size, then a gap of 0.4·title, then the subtitle); wrapped lines
        // advance by 1.15 × their own size.
        var cursor = 0, boxW = 0;
        titleLines.forEach(function (text, i) {
            var width = measure(text, tPx, 700);
            rows.push({ kind: 'title', text: text, px: tPx, width: width, baseline: cursor + tPx * 0.85 });
            cursor += (i < titleLines.length - 1) ? tPx * 1.15 : tPx;
            if (width > boxW) boxW = width;
        });
        if (titleLines.length && subLines.length) cursor += tPx * 0.4;
        subLines.forEach(function (text, i) {
            var width = measure(text, sPx, 400);
            rows.push({ kind: 'sub', text: text, px: sPx, width: width, baseline: cursor + sPx * 0.85 });
            cursor += (i < subLines.length - 1) ? sPx * 1.15 : sPx;
            if (width > boxW) boxW = width;
        });
        var boxH = cursor;
        if (!rows.length) return { box: { x: 0, y: 0, w: 0, h: 0 }, lines: [], align: align, s: s };

        var fx = num(o.titleX) ? o.titleX : 0.5;
        var top = num(o.titleY)
            ? place(o.titleY, h, TEXT_MARGIN_Y, boxH)
            : place(PRESET_Y.hasOwnProperty(o.titlePos) ? PRESET_Y[o.titlePos] : 0.5, h, TEXT_PRESET_MARGIN_Y, boxH);
        var left = place(fx, w, TEXT_MARGIN_X, boxW);

        rows.forEach(function (r) {
            r.baseline += top;
            r.x = align === 'left' ? left : align === 'right' ? left + boxW : left + boxW / 2;
        });
        return { box: { x: left, y: top, w: boxW, h: boxH }, lines: rows, align: align, s: s };
    }

    /* Free-space fractions of a text box whose top-left the user dragged to
       (left, top). */
    function textPositionFrom(o, left, top, boxW, boxH) {
        return { x: unplace(left, o.w, TEXT_MARGIN_X, boxW), y: unplace(top, o.h, TEXT_MARGIN_Y, boxH) };
    }

    /* Visualiser box. vizX/vizY win when set; otherwise the 3×3 preset code
       ('tl','mc','br', …) — which lands on exactly the rectangle the tool drew
       before free placement existed. */
    function vizRect(w, h, sizePct, pos, vizX, vizY) {
        var vw = w * sizePct / 100, vh = h * VIZ_H;
        pos = String(pos || 'bc');
        var px = pos.indexOf('l') >= 0 ? 0 : pos.indexOf('r') >= 0 ? 1 : 0.5;
        var py = pos.charAt(0) === 't' ? 0 : pos.charAt(0) === 'b' ? 1 : 0.5;
        var fx = num(vizX) ? vizX : px;
        var fy = num(vizY) ? vizY : py;
        return { x: place(fx, w, VIZ_MARGIN_X, vw), y: place(fy, h, VIZ_MARGIN_Y, vh), w: vw, h: vh };
    }
    function vizPositionFrom(w, h, sizePct, left, top) {
        var vw = w * sizePct / 100, vh = h * VIZ_H;
        return { x: unplace(left, w, VIZ_MARGIN_X, vw), y: unplace(top, h, VIZ_MARGIN_Y, vh) };
    }

    /* Is the point (px, py) on a box, with `pad` px of grace around it (a thin
       subtitle is hard to grab with a finger)? */
    function hit(box, px, py, pad) {
        pad = pad || 0;
        return !!box && box.w > 0 && box.h > 0
            && px >= box.x - pad && px <= box.x + box.w + pad
            && py >= box.y - pad && py <= box.y + box.h + pad;
    }

    /* Snap a fraction to 0.5 (centre) and to the edges when the pointer is
       within `tolPx` of that spot on an axis `total` px long. Returns
       { v, snapped } so the preview can draw a guide. */
    function snap(frac, total, marginFrac, size, tolPx) {
        var targets = [0.5, 0, 1];
        var at = place(frac, total, marginFrac, size);
        for (var i = 0; i < targets.length; i++) {
            if (Math.abs(place(targets[i], total, marginFrac, size) - at) <= tolPx) return { v: targets[i], snapped: targets[i] };
        }
        return { v: clamp01(frac), snapped: null };
    }

    var api = {
        textLayout: textLayout,
        textPositionFrom: textPositionFrom,
        vizRect: vizRect,
        vizPositionFrom: vizPositionFrom,
        snap: snap,
        hit: hit,
        wrap: wrap,
        place: place,
        PRESET_Y: PRESET_Y,
        TEXT_MARGIN_X: TEXT_MARGIN_X, TEXT_MARGIN_Y: TEXT_MARGIN_Y, TEXT_PRESET_MARGIN_Y: TEXT_PRESET_MARGIN_Y, VIZ_MARGIN_X: VIZ_MARGIN_X, VIZ_MARGIN_Y: VIZ_MARGIN_Y,
        version: '1.0.0'
    };
    root.A2VLayout = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
